import { useQuery } from '@tanstack/react-query'
import { useAccess } from '@/access/useAccess'
import type { SectionId } from '@/access/sections'
import {
  pendingPollsQuery,
  pendingPostsQuery,
  reportedPollsQuery,
  reportedPostsQuery,
} from '@/features/approvals/api'
import { bookingRequestsQuery } from '@/features/bookings/api'
import { deskListQuery } from '@/features/desk/api'
import { deskBatches, lobbyGuests } from '@/lib/desk'

export type BadgeCounts = Partial<Record<SectionId, number>>

/**
 * Sidebar counts, derived from the cached queries (only fetched for sections the person can open):
 * approvals = pending posts + polls + reported; bookings = pending; desk = waiting batches + lobby guests.
 */
export function useBadgeCounts(): BadgeCounts {
  const { condoId, sections } = useAccess()
  const on = (id: SectionId) => sections.some((s) => s.id === id)

  const posts = useQuery({ ...pendingPostsQuery(condoId), enabled: on('approvals') })
  const polls = useQuery({ ...pendingPollsQuery(condoId), enabled: on('approvals') })
  const reportedPosts = useQuery({ ...reportedPostsQuery(condoId), enabled: on('approvals') })
  const reportedPolls = useQuery({ ...reportedPollsQuery(condoId), enabled: on('approvals') })
  const bookings = useQuery({
    ...bookingRequestsQuery(condoId, 'pending'),
    enabled: on('bookings'),
  })
  const desk = useQuery({ ...deskListQuery(condoId, 'waiting'), enabled: on('desk') })

  const counts: BadgeCounts = {}
  if (posts.data && polls.data && reportedPosts.data && reportedPolls.data) {
    counts.approvals =
      posts.data.length + polls.data.length + reportedPosts.data.length + reportedPolls.data.length
  }
  if (bookings.data) counts.bookings = bookings.data.length
  if (desk.data) {
    counts.desk = deskBatches(desk.data.entries).length + lobbyGuests(desk.data.entries).length
  }
  return counts
}
